/* eslint-disable no-nested-ternary */
/* eslint-disable no-param-reassign */

import { InsightTypes, Timeframes } from '@core/insights';
import { addDays } from '@core/utils/dates';
import { sortByCreation } from '@core/utils/sort-by';
import { WellnessCheckValuesWithCreation } from '@web/services/tmapi';
import {
  format,
  getDay,
  getWeek,
  isSameDay,
  isSameMonth,
  isSameWeek,
} from 'date-fns';

export const SIZE = 500;
export const RADIUS = SIZE - 50;
export const POINT_DISTANCE = SIZE / 40;
export const POINT_SIZE = SIZE / 125;
export const POINT_HEIGHT = 2.5;
export const POINT_WIDTH = 1.75;
export const POINT_BLUR = 0; // SIZE / 3336;
export const POINT_SHADOW = 0; // SIZE / 468;

export enum DaysOfTheWeek {
  Su = 'Su',
  Mo = 'Mo',
  Tu = 'Tu',
  We = 'We',
  Th = 'Th',
  Fr = 'Fr',
  Sa = 'Sa',
}

export enum DaysOfTheWeekRotations {
  Su = -90,
  Mo = -140,
  Tu = 168,
  We = 115,
  Th = 65,
  Fr = 12,
  Sa = -40,
}

export enum DayColors {
  Su = '#b526fb',
  Mo = '#bc3cfb',
  Tu = '#cb67fc',
  We = '#d37dfd',
  Th = '#e1a8fd',
  Fr = '#e9befe',
  Sa = '#f8e9ff',
  TODAY = '#EF37ED',
}

export enum WeekColors {
  FIRST = '#6E2696',
  SECOND = '#9142BF',
  THIRD = '#B76FE2',
  FOURTH = '#D394F7',
  FIFTH = '#F1D1FF',
}

export enum PointColors {
  COLOR_0 = '#dd776e',
  COLOR_10 = '#e2886c',
  COLOR_20 = '#e79a69',
  COLOR_30 = '#ecac67',
  COLOR_40 = '#e9b861',
  COLOR_50 = '#f5ce62',
  COLOR_60 = '#d4c86a',
  COLOR_70 = '#b0be6e',
  COLOR_80 = '#94bd77',
  COLOR_90 = '#73b87e',
  COLOR_100 = '#57bb8a',
  TRANSPARENT = 'transparent',
  GRAY = 'lightgray',
}

export type DayOfActivity = {
  weekNumber: number;
  dayLine: DayLine;
  x: number;
  y: number;
  color?: PointColors;
  size?: number;
  blur?: number;
  shadow?: number;
  rotation?: number;
};

export type Point = { x: number; y: number };

export const pointColors = Object.values(PointColors).filter(
  (color) => color !== PointColors.TRANSPARENT && color !== PointColors.GRAY,
);
export const dayColors = Object.values(DayColors);
export const weekColors = Object.values(WeekColors);
export const daysOfTheWeek = Object.values(DaysOfTheWeek);

export const NUMBER_OF_DAYS = daysOfTheWeek.length;
export const DAY_ANGLE = 360 / NUMBER_OF_DAYS;

export type DayLine = {
  day: DaysOfTheWeek;
  color: string;
  point1: Point;
  point2: Point;
};
export const getDayLines = ({
  size = SIZE,
  radius = RADIUS,
}: {
  size: number;
  radius: number;
}): Array<DayLine> =>
  daysOfTheWeek.map((day, idx) => {
    const { x: x1, y: y1 } = findEndPointOnCircle({
      angleInDegrees: DAY_ANGLE * idx,
      size,
      radius,
    });

    return {
      day,
      color: '#FAFAFF',
      point1: {
        x: x1,
        y: y1,
      },
      point2: {
        x: size,
        y: size,
      },
    };
  });

/**
 *
 * # filterByTimeframe
 *
 * A function used to filter hope wnd wellness by the specified timeframe.
 */
export function filterByTimeframe<T>(
  /** @param {Timeframes} timeframe */
  timeframe: Timeframes,
  /** @param {(string | null)} date */
  date: string | null,
  /** @param {(Array<T & { created_on: string | number }> | null)} [documents] */
  documents?: Array<T & { created_on: string | number }> | null,
) {
  if (!documents || !date) {
    return [] as Array<T>;
  }

  return [...documents].sort(sortByCreation).filter((doc) => {
    switch (timeframe) {
      case 'daily': {
        return isSameDay(new Date(date), new Date(doc.created_on));
      }
      case 'weekly': {
        return isSameWeek(new Date(date), new Date(doc.created_on));
      }
      case 'monthly': {
        return isSameMonth(new Date(date), new Date(doc.created_on));
      }
      case 'overview': {
        return doc;
      }
      default: {
        throw new Error('Timeframe not found please choose another timeframe.');
      }
    }
  });
}

export function convertDegreesToRadians(degrees: number) {
  return degrees * (Math.PI / 180);
}

export function findEndPointOnCircle({
  angleInDegrees,
  size = SIZE,
  radius = RADIUS,
}: {
  angleInDegrees: number;
  size?: number;
  radius?: number;
}) {
  const angleInRadians = convertDegreesToRadians(angleInDegrees);
  const x = Math.cos(angleInRadians) * radius + size;
  const y = Math.sin(angleInRadians) * radius + size;

  return { x, y };
}

/**
 * @see https://stackoverflow.com/questions/34372787/javascript-get-position-between-two-points-by-given-length
 */
export function findPointOnLine({
  distance,
  point1,
  point2,
}: {
  distance: number;
  point1: { x: number; y: number };
  point2: { x: number; y: number };
}) {
  const xDist = point1.x - point2.x;
  const yDist = point1.y - point2.y;
  const dist = Math.sqrt(xDist * xDist + yDist * yDist);
  const fractionOfDistance = distance / dist;

  return {
    x: point2.x + xDist * fractionOfDistance,
    y: point2.y + yDist * fractionOfDistance,
  };
}

export function findMidpointOnLine({
  point1,
  point2,
}: {
  point1: { x: number; y: number };
  point2: { x: number; y: number };
}) {
  const x = (point1.x + point2.x) / 2;
  const y = (point1.y + point2.y) / 2;

  return { x, y };
}

export type GroupedActivities = ReturnType<typeof groupActivitiesByDate>;
export function groupActivitiesByDate(
  activities: WellnessCheckValuesWithCreation[],
  date?: string | null,
) {
  if (!date) return {};

  const last30days = Array.from({ length: 30 }, (_, idx) => idx).map(
    (idx) => format(+addDays(-idx, date), 'PP'), // Apr 29, 2021
  );

  return last30days.reverse().reduce(
    (prev, curr) => {
      const dayIndex = getDay(new Date(curr));
      const day = daysOfTheWeek[dayIndex];
      const dayAngle = DAY_ANGLE * dayIndex;

      prev[curr] = { day, dayIndex, dayAngle };

      activities
        .filter((act) => curr === format(new Date(act.created_at), 'PP'))
        .forEach((act) => {
          prev[curr].wellnessCheck = act;
        });

      return prev;
    },
    {} as {
      [date: string]: {
        day: DaysOfTheWeek;
        dayIndex: number;
        dayAngle: number;
        wellnessCheck?: WellnessCheckValuesWithCreation;
      };
    },
  );
}

export type PointConfig = {
  distance: number;
  size: number;
  shadow: number;
  blur: number;
  height: number;
  width: number;
};

export const defaultPointConfig = {
  distance: POINT_DISTANCE,
  size: POINT_SIZE,
  shadow: POINT_SHADOW,
  blur: POINT_BLUR,
  height: POINT_HEIGHT,
  width: POINT_WIDTH,
};

export type ActivityPoints = ReturnType<typeof mapActivitiesToPoints>;
export function mapActivitiesToPoints(
  groupedActivites: GroupedActivities,
  type: InsightTypes,
  config: PointConfig = defaultPointConfig,
  dayLines: ReturnType<typeof getDayLines>,
) {
  return Object.entries(groupedActivites).map(([date, act], idx) => {
    const dayLine = dayLines.find((line) => line.day === act.day);

    if (!dayLine) return;

    const point = findPointOnLine({
      distance: idx * config.distance,
      point1: dayLine.point1,
      point2: dayLine.point2,
    });

    const rotations = [90, -30, 15, 60, 110, 160, 45];

    const wellnessCheck = act.wellnessCheck;
    const colorIdx: number | undefined = wellnessCheck ? wellnessCheck[type] : null;

    const color =
      typeof colorIdx === 'number'
        ? pointColors[colorIdx]
        : PointColors.TRANSPARENT;

    // eslint-disable-next-line consistent-return
    return {
      ...point,
      date,
      dayLine,
      weekNumber: getWeek(new Date(date)),
      color,
      size: idx * config.size,
      shadow: idx * config.shadow,
      blur: idx * config.blur,
      rotation: rotations[act.dayIndex],
    };
  });
}

export type WeekLine = ReturnType<typeof getWeekLines>;
export function getWeekLines(points: ActivityPoints) {
  return points
    .reduce(
      (prev, curr, idx, arr) => {
        if (!curr) return prev;

        const previous = arr[idx - 1];
        const previousWeekNumber = previous
          ? getWeek(new Date(previous.date))
          : null;

        const { weekNumber } = curr;
        const weekColorIdx = !previousWeekNumber
          ? 0
          : curr.weekNumber > previousWeekNumber
          ? prev.currentWeekColorIdx + 1
          : prev.currentWeekColorIdx;
        const weekColor = weekColors[weekColorIdx];

        return {
          currentWeekColorIdx: weekColorIdx,
          points: [
            ...prev.points,
            {
              ...curr,
              weekNumber,
              weekColor,
            },
          ],
        };
      },
      { currentWeekColorIdx: 0, points: [] } as {
        currentWeekColorIdx: number;
        points: Array<{
          x: number;
          y: number;
          size: number;
          blur: number;
          shadow: number;
          rotation: number;
          weekNumber: number;
          date: string;
          dayLine: DayLine;
          color: PointColors;
          weekColor: WeekColors;
        }>;
      },
    )
    .points.reduce(
      (prev, curr, idx, arr) => {
        const { weekColor } = curr;
        const { weekNumber } = curr;

        const center = curr?.dayLine.point2;
        const currBottom = arr[idx - 7] || center; // a week ago || the center
        const currTop = arr[idx + 7] || curr?.dayLine.point1; // a week ahead || the top of dayline

        const nextIdx = idx + 1;
        const next = arr[nextIdx];
        const nextBottom = arr[nextIdx - 7] || center; // a week ago || the center
        const nextTop = arr[nextIdx + 7] || next?.dayLine.point1; // a week ahead || the top of next's dayline

        if (
          !curr ||
          !next ||
          !nextTop ||
          !nextBottom ||
          !currTop ||
          !currBottom
        ) {
          return prev;
        }

        const point1 = findMidpointOnLine({
          point1: { x: currTop.x, y: currTop.y },
          point2: { x: curr.x, y: curr.y },
        });

        const point2 = findMidpointOnLine({
          point1: { x: nextTop.x, y: nextTop.y },
          point2: { x: next.x, y: next.y },
        });

        const point3 = findMidpointOnLine({
          point1: { x: next.x, y: next.y },
          point2: { x: nextBottom.x, y: nextBottom.y },
        });

        const point4 = findMidpointOnLine({
          point1: { x: curr.x, y: curr.y },
          point2: { x: currBottom.x, y: currBottom.y },
        });

        const midpoint = findMidpointOnLine({ point1, point2: point3 });
        const curveControl = findMidpointOnLine({
          point1: currTop,
          point2: nextTop,
        });

        return [
          ...prev,
          {
            point1,
            point2,
            point3,
            point4,
            midpoint,
            weekColor,
            weekNumber,
            curveControl,
          },
        ];
      },
      [] as Array<{
        point1: { x: number; y: number };
        point2: { x: number; y: number };
        point3: { x: number; y: number };
        point4: { x: number; y: number };
        midpoint: { x: number; y: number };
        curveControl: { x: number; y: number };
        weekNumber: number;
        weekColor: string;
      }>,
    );
}
